// V2 PDP entry script.
//
// Bundled into the `productDetailV2` webpack entry (alongside the React
// ImageGrid and the LESS) and emitted at the end of <body>, so the DOM is
// already parsed when most of this runs. `setupButton()` is deferred to
// DOMContentLoaded to match V1 timing with ProductFavorites.init().

import '/Foundation/Common/code/Views/Foundation/Common/AdobeAnalytics/TooltipOpenTracking.js';
import { setupButton } from '/Foundation/Common/code/Views/Foundation/Common/AddToCartButton.js';
import { loadPayPalSDK, renderPayPalMessage } from '/Foundation/Common/code/Utils/PayPalMessaging';
import { getMercadoPaymentMessage } from '/Foundation/Common/code/Utils/PaymentUtils';
import { setupFilterDropdown } from '../ProductDetail_FilterDropdown.ts';
import ProductDetail_Tracking from '../ProductDetail_Tracking.js';
import { initShippingCalculator } from '../ShippingCalculator.ts';

type PdpBadge = { CategoryId?: string };

const badgePageFlags = (badges?: PdpBadge[]) => ({
  badges: badges?.some((b) => b.CategoryId !== 'Features') ?? false,
  badges_informative: badges?.some((b) => b.CategoryId === 'Informative') ?? false,
  badges_commercial: badges?.some((b) => b.CategoryId === 'Commercial') ?? false,
  badges_feature: badges?.some((b) => b.CategoryId === 'Features') ?? false,
});

const boolStr = (value: boolean) => (value ? 'True' : 'False');

const initAdobeProductData = () => {
  if (!window.adobeDataLayer) return;

  const pd = (window as any)._THULEDATA?.ProductDetail;
  if (!pd) return;

  window.adobeDataLayer.products = [
    {
      id: pd.ProductId || '',
      name: pd.WebTitle1 || '',
      subcategory: pd.Subcategory || '',
      category: pd.Category || '',
      over_category: pd.OverCategory || '',
      colour: pd.ColorSpecification || '',
      stock_status: pd.StockStatusAnalytics || '',
      outlet_product: boolStr(pd.IsOutlet === true),
    },
  ];

  if (window.adobeDataLayer.page) {
    const badgeFlags = badgePageFlags(pd.Badges);
    window.adobeDataLayer.page.title = pd.WebTitle1 || '';
    window.adobeDataLayer.page.category = pd.Category || '';
    window.adobeDataLayer.page.subcategory = pd.Subcategory || '';
    window.adobeDataLayer.page.over_category = pd.OverCategory || '';
    window.adobeDataLayer.page.badges = boolStr(badgeFlags.badges);
    window.adobeDataLayer.page.badges_informative = boolStr(badgeFlags.badges_informative);
    window.adobeDataLayer.page.badges_commercial = boolStr(badgeFlags.badges_commercial);
    window.adobeDataLayer.page.badges_feature = boolStr(badgeFlags.badges_feature);
  }
};

initAdobeProductData();

// Shows the size dropdown instead of the chips when the chips wrap onto a
// second row. Both are rendered; the chips are shown to measure them, which
// happens within one frame, so nothing flickers while resizing.
const fitVariantChips = () => {
  const dropdown = document.querySelector('[data-variant-dropdown]');
  const chips = dropdown?.previousElementSibling as HTMLElement;
  if (!chips) return;

  chips.classList.remove('d-none');
  const items = chips.children as HTMLCollectionOf<HTMLElement>;
  const wraps = items[items.length - 1].offsetTop > items[0].offsetTop;
  chips.classList.toggle('d-none', wraps);
  dropdown.classList.toggle('d-none', !wraps);
  dropdown.setAttribute('data-fitted', '');
};

fitVariantChips();
document.fonts?.ready.then(fitVariantChips);
const variantGroup = document.querySelector('[data-variant-dropdown]')?.parentElement;
if (variantGroup) new ResizeObserver(fitVariantChips).observe(variantGroup);

// Reveals the right `*-btn-section` (buyable / out-of-stock / notify-me / ...)
// based on the inventory check, and wires up the click handler on the
// "Add to cart" button. Deferred to DOMContentLoaded so the inventory fetch
// starts in parallel with ProductFavorites.init(), matching V1 behaviour.
// Flowbox should only be visible on PDP if at least 6 posts are connected to the product.
const initFlowboxVisibility = () => {
  const flowboxContainer = document.querySelector('#js-flowbox-flow');
  const ugcSection = document.querySelector('.ugc-section');
  if (!ugcSection || !flowboxContainer) return;

  const resizeObserver = new ResizeObserver(() => {
    if (!document.querySelector('.bzfy-t-feed-tile-grid')) return;

    const posts = flowboxContainer.querySelectorAll('.bzfy-o-layout__item');
    if (posts?.length >= 6) {
      ugcSection.classList.remove('hide-flowbox');
    } else {
      (window as any).flowbox?.('destroy', { container: '#js-flowbox-flow' });
      ugcSection.classList.add('d-none');
    }
  });

  resizeObserver.observe(flowboxContainer);
};

document.addEventListener('DOMContentLoaded', () => {
  const addToCartArea = document.querySelector('.product-add-to-cart');
  const isRoofRackProduct = addToCartArea?.querySelector<HTMLInputElement>('.atc-roofrack-product')?.value;
  if (!isRoofRackProduct) setupButton();

  if (document.querySelector('.thule-filter-select')) setupFilterDropdown();

  initShippingCalculator();
  ProductDetail_Tracking?.init();
  initFlowboxVisibility();
});

// Payment messaging — reads config from the _THULEDATA.ProductDetail blob
// emitted server-side. Each provider is guarded on its config fields being
// present AND the container div existing in the DOM (the Razor partial gates
// on enabled + price eligibility, so if the div isn't there the provider
// is either disabled or the price is out of range).
const pd = (window as any)._THULEDATA?.ProductDetail;

if (pd?.PayPalBaseURL && pd?.PayPalClientId) {
  loadPayPalSDK({
    clientId: pd.PayPalClientId,
    currency: pd.CurrencyCode,
    baseURL: pd.PayPalBaseURL,
  }).then(() => {
    renderPayPalMessage({
      selector: '.paypal-messaging-product',
      amount: pd.EffectivePrice,
      placement: 'product',
      buyerCountry: pd.BuyerCountry,
    });
  });
}

if (pd?.MercadoBaseURL && pd?.MercadoAPIKey && document.querySelector('.mercado-messaging')) {
  getMercadoPaymentMessage(pd.MercadoBaseURL, pd.MercadoAPIKey, pd.EffectivePrice);
}

// TabArea switcher — wires up the tab strip and the shared Features /
// Manuals arrows. Arrows query the visible `.pdp__featuresSlider` on demand
// (no cached slider, no bind/unbind). One card per click so we land on a
// `scroll-snap` point. Hidden when the active panel has no overflowing row.
const initTabs = (root: HTMLElement) => {
  const tabs = Array.from(root.querySelectorAll<HTMLButtonElement>('.pdp__tab[role="tab"]'));
  const panels = Array.from(root.querySelectorAll<HTMLElement>('.pdp__tabPanel[role="tabpanel"]'));
  if (tabs.length === 0 || panels.length === 0) return;

  const arrows = root.querySelector<HTMLElement>('.pdp__tabArrows');
  const prevBtn = root.querySelector<HTMLButtonElement>('.pdp__tabArrow:not(.pdp__tabArrow--next)');
  const nextBtn = root.querySelector<HTMLButtonElement>('.pdp__tabArrow--next');

  const activeSlider = () => root.querySelector<HTMLElement>('.pdp__tabPanel--active .pdp__featuresSlider');

  const updateArrows = () => {
    root.querySelectorAll<HTMLElement>('.pdp__featuresSlider').forEach((slider) => {
      slider.classList.toggle('has-overflow', slider.scrollWidth > slider.clientWidth);
    });

    if (!arrows || !prevBtn || !nextBtn) return;
    const slider = activeSlider();
    const overflowing = !!slider && slider.classList.contains('has-overflow');
    arrows.hidden = !overflowing;
    if (!slider || !overflowing) return;
    prevBtn.disabled = slider.scrollLeft === 0;
    nextBtn.disabled = slider.scrollLeft + slider.clientWidth >= slider.scrollWidth - 1;
  };

  const scrollByCard = (direction: -1 | 1) => {
    const slider = activeSlider();
    if (!slider) return;
    const card = slider.querySelector<HTMLElement>('.pdp__featureCard');
    const gap = parseFloat(getComputedStyle(slider).gap) || 0;
    slider.scrollBy({ left: direction * ((card?.offsetWidth || slider.clientWidth) + gap), behavior: 'smooth' });
  };

  prevBtn?.addEventListener('click', () => scrollByCard(-1));
  nextBtn?.addEventListener('click', () => scrollByCard(1));
  root.addEventListener(
    'scroll',
    (e) => {
      if ((e.target as HTMLElement).classList?.contains('pdp__featuresSlider')) updateArrows();
    },
    true
  );
  window.addEventListener('resize', updateArrows);

  const manualsHost = root.querySelector('#image-navigation');
  if (manualsHost) {
    new MutationObserver(updateArrows).observe(manualsHost, { childList: true, subtree: true });
  }

  const setActive = (index: number) => {
    tabs.forEach((tab, i) => {
      const isActive = i === index;
      tab.classList.toggle('pdp__tab--active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    panels.forEach((panel, i) => {
      const isActive = i === index;
      panel.classList.toggle('pdp__tabPanel--active', isActive);
      panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    requestAnimationFrame(updateArrows);
  };

  tabs.forEach((tab, index) => {
    tab.addEventListener('click', () => setActive(index));
    tab.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setActive(index);
      }
    });
  });

  updateArrows();
};

document.querySelectorAll<HTMLElement>('.pdp__tabs').forEach(initTabs);

// Buy-column BV rating summary → Reviews tab. The mount is an <a> so it is
// keyboard-reachable before BV hydrates; we still preventDefault because
// #pdp__panel-reviews is display:none until the tab is active and a raw
// hash jump would miss. "Write a review" (`.bv_war_button`) stays with BV.
// The hover card is appended to document.body, so its Read reviews button
// is not inside the anchor. Match that button by structure — the label is localized.
const reviewsHashes = new Set(['#pdp__panel-reviews', '#pdp__reviews']);

const isWriteReviewControl = (target: EventTarget | null) => {
  const el = target instanceof Element ? target : null;
  return !!el?.closest('.bv_war_button, .bv_writeReviewButton, [class*="bv_writeReview"], [class*="bv_write_review"]');
};

// Histogram "Read reviews" is the only button in the portaled card.
// Write a review uses `.bv_war_button` and is not in this card.
const isReadReviewsOverlayButton = (target: EventTarget | null) => {
  const el = target instanceof Element ? target : null;
  const button = el?.closest('button');
  if (!button || isWriteReviewControl(button)) return false;
  return !!button.closest('[data-bv-modal] .bv_button_component_container');
};

const openReviewsTab = () => {
  document.getElementById('pdp__tab-reviews')?.click();
  document.querySelector<HTMLElement>('.pdp__tabs')?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  });
};

document.getElementById('pdp__reviewsLink')?.addEventListener(
  'click',
  (e) => {
    e.preventDefault();
    if (isWriteReviewControl(e.target)) return;
    openReviewsTab();
  },
  true
);

document.addEventListener(
  'click',
  (e) => {
    const target = e.target instanceof Element ? e.target : null;
    if (!target || !isReadReviewsOverlayButton(target)) return;
    if (target.closest('[data-bv-show="reviews"], #pdp__panel-reviews')) return;
    // Other rating summaries (related products) portal their own cards.
    if (!document.querySelector('#pdp__reviewsLink [aria-expanded="true"]')) return;
    e.preventDefault();
    openReviewsTab();
  },
  true
);

if (reviewsHashes.has(window.location.hash)) {
  openReviewsTab();
}

// Open the first accordion below the image grid. Gating lives in Razor; this
// only picks whichever <details> rendered first inside each container.
document.querySelectorAll<HTMLElement>('.pdp__accordionContainer').forEach((container) => {
  const firstAccordion = container.querySelector<HTMLDetailsElement>('details.pdpAccordion');
  if (firstAccordion) firstAccordion.open = true;
});

// Smooth slide open/close for every <details.pdpAccordion> on the page
// (promotion box, main accordion stack, FAQ items). Pure-CSS height:auto
// animation (`interpolate-size`) is still Chromium-only, so we animate the
// <details> element's height with the Web Animations API instead — works in
// Chrome, Firefox and Safari, and falls back to the native instant toggle
// when JS is unavailable or the user prefers reduced motion. Delegated on
// document so it covers all current and future accordions without per-element
// wiring. Keyboard activation (Enter/Space on <summary>) also fires a click,
// so it goes through the same path.
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

document.addEventListener('click', (e) => {
  const summary = (e.target as Element | null)?.closest<HTMLElement>('summary.pdpAccordion__header');
  if (!summary || prefersReducedMotion.matches) return;

  const details = summary.parentElement as HTMLDetailsElement | null;
  if (!details || !details.classList.contains('pdpAccordion')) return;

  e.preventDefault();
  if (details.dataset.animating) return;

  const animateHeight = (from: number, to: number, onFinish?: () => void) => {
    details.dataset.animating = 'true';
    // Clip the content while the height is pinned by the animation, otherwise
    // the not-yet-revealed (or not-yet-hidden) content bleeds out of the box.
    details.style.overflow = 'clip';
    // fill:'forwards' keeps the end height pinned through the onFinish
    // callback (where [open] is flipped on close) so there's no single-frame
    // flash of the fully expanded box; cancel() then releases the pin.
    const animation = details.animate(
      { height: [`${from}px`, `${to}px`] },
      { duration: 250, easing: 'ease', fill: 'forwards' }
    );
    animation.oncancel = () => {
      delete details.dataset.animating;
      details.style.overflow = '';
    };
    animation.onfinish = () => {
      onFinish?.();
      animation.cancel();
    };
  };

  const startHeight = details.offsetHeight;
  if (details.open) {
    // Keep [open] (and thus the rendered content) during the slide-up;
    // only flip it once the box has shrunk to just the header.
    animateHeight(startHeight, summary.offsetHeight, () => {
      details.open = false;
    });
  } else {
    details.open = true;
    animateHeight(startHeight, details.offsetHeight);
  }
});
